import React, { memo, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import studyService from '../../services/studyService';
import siteService from '../../services/siteService';
import { AuthContext } from '../../contexts/AuthContext';
import Sites from '../Site/Sites';
import StudyMFASettings from './StudyMFASettings';
import StudyDeviceConfig from './StudyDeviceConfig';
import DCQs from '../DCQ/DCQs';
import StudyCaregiversTab from '../Caregivers/StudyCaregiversTab';
import { Activity, Shield, FileText, Briefcase, ChevronLeft, Edit, Loader, BarChart, Upload, UserPlus, Users, UserCog, Bell, Book, Smartphone } from 'lucide-react';
import PageHeader from '../Shared/PageHeader';
import ContextualNotificationToggle from '../Notifications/ContextualNotificationToggle';
import { Tab } from '@headlessui/react';
import AddUserModal from '../User/AddUserModal';
import BulkCreateParticipantsModal from '../Participant/BulkCreateParticipantsModal';
import { useLocation } from 'react-router-dom';
import hasUserRole from '../../utils/UserUtil'
import studyUtil from '../../utils/StudyUtil'
import permissionsService from '../../services/permissionsService'

const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
  const year = date.getFullYear();
  return `${day}${month}${year}`;
};

const getTabsForUser = (user, canManageSecurity = false, study = null) => {
  // TODO: Eventually consolidate device config, portal settings, and other study
  // settings into a unified "Configuration" tab or a dedicated study settings page.
  // For now, device config lives as its own tab. See also EditStudy.jsx.
  const isInternal = user?.is_internal || user?.is_superuser;

  const baseTabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'sites', label: 'Sites', icon: Briefcase },
    { id: 'dcqs', label: 'DCQs', icon: FileText },
  ];

  // Devices tab — internal users only (site staff don't manage device pools).
  if (isInternal) {
    baseTabs.push({ id: 'devices', label: 'Devices', icon: Smartphone });
  }

  // Caregivers tab — superuser/study staff only, gated on study toggle (FECOA-1320).
  if (study?.allow_caregivers && (user?.is_superuser || canManageSecurity)) {
    baseTabs.push({ id: 'caregivers', label: 'Caregivers', icon: UserCog });
  }

  // Only show Security tab to internal users with manage_study_security permission
  if (isInternal && (canManageSecurity || user?.is_superuser)) {
    baseTabs.push({ id: 'security', label: 'Security', icon: Shield });
  }

  // Add Notifications tab for all authenticated users
  baseTabs.push({ id: 'notifications', label: 'Notifications', icon: Bell });

  return baseTabs;
};

const SingleStudy = memo(() => {
  const { studyID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useContext(AuthContext);

  const [study, setStudy] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
  const [hasOnlyOneStudy, setHasOnlyOneStudy] = useState(null); // null = checking, false = multiple studies, true = one study
  const [canManageSecurity, setCanManageSecurity] = useState(false);
  const [permissions, setPermissions] = useState({
    canChangeStudy: false,
    canAddUser: false,
    canAddSite: false,
    canBulkCreateParticipants: false,
  });

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (user) {
        try {
          const [hasSecurityPermission, hasChangeStudy, hasAddUser, hasAddSite, hasBulkCreate] = await Promise.all([
            permissionsService.hasPermission(permissionsService.PERMISSIONS.MANAGE_STUDY_SECURITY),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.CHANGE_STUDY),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_USER),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.ADD_SITE),
            permissionsService.hasPermission(permissionsService.PERMISSIONS.BULK_CREATE_PARTICIPANTS),
          ]);

          setCanManageSecurity(hasSecurityPermission);
          setPermissions({
            canChangeStudy: hasChangeStudy,
            canAddUser: hasAddUser,
            canAddSite: hasAddSite,
            canBulkCreateParticipants: hasBulkCreate,
          });

          // Debug logging
          console.log('[SINGLE_STUDY] Permissions set:', {
            canChangeStudy: hasChangeStudy,
            canAddUser: hasAddUser,
            canAddSite: hasAddSite,
            canBulkCreateParticipants: hasBulkCreate,
          });
        } catch (error) {
          console.error('Error checking permissions:', error);
          setCanManageSecurity(false);
          setPermissions({
            canChangeStudy: false,
            canAddUser: false,
            canAddSite: false,
            canBulkCreateParticipants: false,
          });
        }
      }
    };
    
    checkPermissions();
  }, [user]);

  // Get tabs based on user permissions
  const tabs = getTabsForUser(user, canManageSecurity, study);

  const getActiveTabFromURL = () => {
    // Check query parameter first (?tab=notifications)
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const t = tabs.findIndex(tab => tab.id === tabParam);
      if (t >= 0) return t;
    }
    
    // Fall back to hash (#notifications)
    if (location.hash) {
      const id = location.hash.substring(1);
      const t = tabs.findIndex(tab => tab.id === id);
      if (t >= 0) return t;
    }
    
    return 0;
  }

  const [activeTab, setActiveTab] = useState(getActiveTabFromURL());

  // Update active tab when URL changes (e.g., clicking "View" from notifications page)
  useEffect(() => {
    const newTab = getActiveTabFromURL();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [location.search, location.hash]);

  const updateActiveTab = (tab) => {
    const tabId = tabs[tab].id;
    if (tabId == 'overview') 
      navigate('', { replace: true });
    else
      navigate(`#${tabs[tab].id}`, { replace: true });
    setActiveTab(tab)
  }

    useEffect(() => {
      const loadStudyData = async () => {
        setLoading(true);
        try {
          const studyData = await studyService.getSingleStudy(studyID);
          const sitesData = await siteService.getSitesForStudy(studyID);
          
          if (studyData) {
            console.log('Study Data:', studyData); // Debug log
            setStudy(studyData);
            setSites(sitesData);
          } else {
            setError('No study data found.');
          }
        } catch (error) {
          console.error('Error fetching study:', error);
          setError('An error occurred while fetching the study.');
        } finally {
          setLoading(false);
        }
      };

      if (isAuthenticated) {
        loadStudyData();
      }
    }, [studyID, isAuthenticated]);

  // Check if user has only one study
  useEffect(() => {
    const checkStudyCount = async () => {
      if (user) {
        try {
          const onlyOneStudy = await studyUtil.hasOnlyOneStudy();
          setHasOnlyOneStudy(onlyOneStudy);
        } catch (error) {
          console.error('Error checking study count:', error);
        }
      }
    };

    checkStudyCount();
  }, [user]);

  // Set active tab from URL once user is loaded
  useEffect(() => {
    if (user) {
      setActiveTab(getActiveTabFromURL());
    }
  }, [user]);

  const handleAddUserSuccess = () => {
    setIsAddUserModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <Loader className="w-16 h-16 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl dark:shadow-gray-900/20">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-700 dark:text-gray-200">{error}</p>
        </div>
      </div>
    );
  }

  const totalParticipants = sites.reduce((sum, site) => sum + (site.total_participants_count || 0), 0);
  const participantsInUse = sites.reduce((sum, site) => sum + (site.active_participants_count || 0), 0);
  const participantsInUsePercentage = totalParticipants > 0 ? (participantsInUse / totalParticipants) * 100 : 0;

  
  
  const studyDetails = {
    totalParticipants,
    participantsInUse
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {hasOnlyOneStudy === false && (
            <Link
              to="/studies"
              className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Studies
            </Link>
          )}
          <PageHeader
            icon={Book}
            title={study?.title ? `${study.title} Study` : 'Study'}
            subtitle="Manage study details, sites, and data queries"
          >
            <Link
              to={`/studies/${studyID}/compliance`}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
            >
              <BarChart className="w-4 h-4 mr-2" />
              Compliance & Reporting
            </Link>

            {permissions.canChangeStudy && (
              <Link
                to={`/studies/${studyID}/edit`}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Study
              </Link>
            )}

            {permissions.canAddSite && (
              <button
                onClick={() => navigate(`/studies/${studyID}/upload`)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Sites & Users
              </button>
            )}

            {permissions.canAddUser && (
              <button
                onClick={() => setIsAddUserModalOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </button>
            )}

            {permissions.canBulkCreateParticipants && (
              <button
                onClick={() => setIsBulkCreateOpen(true)}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-all duration-200 shadow-sm dark:shadow-gray-900/20"
              >
                <Users className="w-4 h-4 mr-2" />
                Bulk Add Participants
              </button>
            )}
          </PageHeader>
          {error ? (
            <div className="text-red-600 mb-4">{error}</div>
          ) : (
            <>
              <AddUserModal
                isOpen={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
                onSuccess={handleAddUserSuccess}
                sponsorId={study?.sponsor?.id}
                studyId={studyID}
              />
              <BulkCreateParticipantsModal
                isOpen={isBulkCreateOpen}
                onClose={() => setIsBulkCreateOpen(false)}
                onSuccess={() => { setIsBulkCreateOpen(false); window.location.reload(); }}
                studyId={studyID}
                sites={sites}
                studyName={study?.title || ''}
                sponsorCode={study?.sponsor?.code || study?.sponsor?.name || ''}
              />
            </>
          )}
          <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900/20 rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700">
              <h2 className="text-xl font-semibold text-white">Study Details</h2>
            </div>
            <div className="p-6">
              <Tab.Group selectedIndex={activeTab} onChange={updateActiveTab}>
                <Tab.List className="flex space-x-4 border-b border-gray-200 dark:border-gray-700 mb-6">
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      className={({ selected }) =>
                        `px-4 py-2 text-sm font-medium leading-5 text-primary-700 focus:outline-none ${
                          selected ? 'border-b-2 border-primary-600' : 'text-gray-500 dark:text-gray-400 hover:text-primary-600'
                        }`
                      }
                    >
                      <div className="flex items-center">
                        <tab.icon size={18} className="mr-2" />
                        {tab.label}
                      </div>
                    </Tab>
                  ))}
                </Tab.List>
                <Tab.Panels>
                  <Tab.Panel>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="flex flex-col gap-6 mb-8">
                        <div 
                          className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 shadow-sm dark:shadow-gray-900/20 flex items-center space-x-4"
                        >
                          <Users className="text-primary-600" size={24} />
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Total Participants</p>
                            <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">{studyDetails.totalParticipants}</p>
                          </div>
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-xl shadow-sm dark:shadow-gray-900/20">
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Participants In Use</p>
                          <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-6 overflow-hidden">
                            <div 
                              style={{ width: `${participantsInUsePercentage}%` }}
                              className="h-full bg-primary-600 rounded-full relative transition-all duration-1000 ease-out"
                            >
                              <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                                {studyDetails.participantsInUse} / {studyDetails.totalParticipants}
                              </span>
                            </div>
                          </div>
                        </div>

                      </div>
                    </motion.div>
                  </Tab.Panel>
                  <Tab.Panel>
                    <Sites studyID={studyID} />
                  </Tab.Panel>
                  <Tab.Panel>
                    <DCQs studyID={studyID} />
                  </Tab.Panel>
                  {tabs.some((t) => t.id === 'devices') && (
                    <Tab.Panel>
                      <StudyDeviceConfig studyID={studyID} canEdit={permissions.canChangeStudy} />
                    </Tab.Panel>
                  )}
                  {/* Conditional panels keep Tab.Panel indices aligned with the
                      dynamic tabs array (caregivers/security may be absent). */}
                  {tabs.some((t) => t.id === 'caregivers') && (
                    <Tab.Panel>
                      <StudyCaregiversTab studyID={studyID} />
                    </Tab.Panel>
                  )}
                  {tabs.some((t) => t.id === 'security') && (
                    <Tab.Panel>
                      <StudyMFASettings studyId={studyID} />
                    </Tab.Panel>
                  )}
                  <Tab.Panel>
                    {/* Notifications Tab */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="max-w-4xl mx-auto">
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm dark:shadow-gray-900/20 overflow-hidden">
                          <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="bg-white dark:bg-gray-900 p-2 rounded-lg shadow-sm dark:shadow-gray-900/20">
                                  <Bell className="h-6 w-6 text-primary-600" />
                                </div>
                                <div>
                                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Study Notifications</h3>
                                  <p className="text-sm text-gray-600 dark:text-gray-300">Manage notification preferences for this study</p>
                                </div>
                              </div>
                              <Link
                                to="/profile?tab=notifications"
                                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors flex items-center gap-1"
                              >
                                <span>Advanced settings</span>
                                <span>→</span>
                              </Link>
                            </div>
                          </div>
                          
                          <div className="p-8">
                            <div className="space-y-4">
                              <ContextualNotificationToggle
                                eventKey="survey.missed"
                                label="Missed Surveys"
                                description="Get notified when participants miss their survey deadlines"
                                studyId={studyID}
                              />
                              
                              <ContextualNotificationToggle
                                eventKey="participant.created"
                                label="New Participants"
                                description="Know when new participants are enrolled in this study"
                                studyId={studyID}
                              />
                              
                              <ContextualNotificationToggle
                                eventKey="dcq.created"
                                label="New Data Queries"
                                description="Get notified when new data clarification queries are created"
                                studyId={studyID}
                              />

                              <ContextualNotificationToggle
                                eventKey="dcq.updated"
                                label="Data Query Updates"
                                description="Be alerted when queries are updated, commented on, or status changes"
                                studyId={studyID}
                              />

                              <ContextualNotificationToggle
                                eventKey="participant.activated"
                                label="Participant Activations"
                                description="Be informed when participants are activated"
                                studyId={studyID}
                              />
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-start gap-2">
                              <span className="text-lg">💡</span>
                              <div className="flex-1">
                                <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-1">About Study Notifications</p>
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                  These notifications are specific to <strong>{study?.title || 'this study'}</strong>. 
                                  You'll only receive alerts for events that happen within this study's sites and participants.
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-2">
                                  For global notification preferences or to configure notifications across all studies, 
                                  visit your <Link to="/profile?tab=notifications" className="text-primary-600 hover:underline font-medium">notification settings</Link>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
});

export default SingleStudy;